import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import { format, getDate, isValid, parse } from 'date-fns';

import {
  getError,
  PayCalendar,
  DeductionsConfigurations as ExternalDeductionsConfigurations,
  CheckOutline,
  useToast,
} from '@aslanpay/portal-common-lib';

import {
  PayFrequency,
  AccrualPeriodType,
  DeductionsConfigurations,
} from 'app/constants/payCalendar';

import useCreatePayCalendar from 'app/hooks/useCreatePayCalendar';

import { useManagingCompanyStore, useCreatePayCalendarStore } from 'app/store';

import { parseCurrencyIntoFloat } from 'app/utils/strings';

import { getOrdinalSuffix } from 'app/utils/date/index';

import {
  PayCalendarAslanDetailsFormFields,
  PayCalendarCutoffInformationFormFields,
  PayCalendarDefineCalendarFormFields,
  PayCalendarCalendarDefaultsFormFields,
  PayCalendarPaydayInformationFormFields,
} from '../CreatePayCalendarForm/types';

import { CreatePayCalendarOverviewModalProps } from './types';

function CreatePayCalendarOverviewModal({
  isOpen,
  onClose,
  onEdit,
  onCreate,
}: CreatePayCalendarOverviewModalProps) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { company } = useManagingCompanyStore.getState();
  const payCalendarData = useCreatePayCalendarStore((state) => state.payCalendarData);
  const resetPayCalendarData = useCreatePayCalendarStore((state) => state.resetPayCalendarData);

  // Page 1 - Define Calendar
  const { name, externalPayCalendarId, currency, payFrequency } =
    payCalendarData as PayCalendarDefineCalendarFormFields;

  // Page 2 - Payday Information
  const {
    payType,
    payday,
    paydayNDayOfPeriod,
    paydayNBusinessDayOfPeriod,
    paydayLastCustomWeekday,
    paydayFirstCustomWeekday, // NEW: First custom weekday for payday
    paydayFixedDay,
    paydayCustomDay,
    startDate,
  } = payCalendarData as PayCalendarPaydayInformationFormFields;

  // Page 3 - Calendar defaults
  const { workingDays, paydayNonBusinessDayFailover, location, defaultAnnualGrossSalary } =
    payCalendarData as PayCalendarCalendarDefaultsFormFields;

  // Page 4 - Cutoff Information
  const {
    payrollCutoff,
    payrollCutoffDays,
    payrollCutoffCustomDay,
    cutOffNonBusinessDayFailover,
    payrollCutoffFixedDay,
  } = payCalendarData as PayCalendarCutoffInformationFormFields;
  // Page 5 - Aslan Details
  const {
    odpAllowancePercentage,
    customOdpAllowancePercentage,
    startingBalanceDays,
    globalAllowancePercentage,
    customGlobalAllowancePercentage, // NEW: Custom Global limit when globalLimit is 200
    accrualPeriod, // NEW: Accrual period configuration
    salariedDeductionsRequestMadeAvailableConfig, // NEW: Salaried deductions config
    nonSalariedDeductionsRequestMadeAvailableConfig, // NEW: Non-salaried deductions config
  } = payCalendarData as PayCalendarAslanDetailsFormFields;

  const { mutate: createPayCalendar, isLoading: isCreatingPayCalendar } = useCreatePayCalendar({
    onSuccess: (payCalendar: PayCalendar) => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'New pay calendar created',
        description: `${payCalendar.name} was successfully created!`,
      });

      if (onCreate) onCreate(payCalendar.id);

      queryClient.invalidateQueries({ queryKey: ['fetchPayCalendarsPage'] });
      queryClient.invalidateQueries({ queryKey: ['fetchPayCalendarsForEmployee'] });

      resetPayCalendarData();

      onClose();
    },
    onError: (error) => {
      const baseError = getError(error);

      if (baseError.codeDescription === 'CONFLICT') {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Pay calendar already exists',
          description:
            'There is already a pay calendar with the inserted name. Please, submit the form with a unique pay calendar name.',
        });
      } else {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: 'Sorry, the new pay calendar was not created. Please try again.',
        });
      }
    },
  });

  const handleEdit = () => {
    onClose();
    onEdit();
  };

  // Now we can have custom amounts for the salary allowance, we need to handle this
  let customOdpAllowancePercentageValue = 0;

  if (odpAllowancePercentage !== undefined) {
    customOdpAllowancePercentageValue = odpAllowancePercentage;
  }

  if (odpAllowancePercentage === 200) {
    if (customOdpAllowancePercentage !== undefined) {
      customOdpAllowancePercentageValue = customOdpAllowancePercentage;
    }
  }

  // Now we can have custom amounts for the global allowance percentage, we need to handle this
  let globalAllowancePercentageValue;

  if (globalAllowancePercentage === 200 && customGlobalAllowancePercentage !== undefined) {
    globalAllowancePercentageValue = customGlobalAllowancePercentage;
  } else {
    globalAllowancePercentageValue = globalAllowancePercentage;
  }

  // eslint-disable-next-line consistent-return
  const handleConfirm = () => {
    const defaultAnnualGrossSalaryParsed = parseCurrencyIntoFloat(
      defaultAnnualGrossSalary?.toString() || ''
    );

    // maps to: N_DAY_OF_PERIOD, N_BUSINESS_DAY_OF_PERIOD, CUSTOM_DAY, CUSTOM_DAY_IN_ARREARS
    let paydayCustomDayValue;

    switch (payday) {
      case 'N_DAY_OF_PERIOD':
        paydayCustomDayValue = paydayNDayOfPeriod;
        break;
      case 'N_BUSINESS_DAY_OF_PERIOD':
        paydayCustomDayValue = paydayNBusinessDayOfPeriod;
        break;
      case 'CUSTOM_DAY':
      case 'CUSTOM_DAY_IN_ARREARS':
        paydayCustomDayValue = paydayCustomDay;
        break;
      default:
        paydayCustomDayValue = undefined;
    }

    let cutOffValue;

    switch (payrollCutoff) {
      case 'N_DAYS_BEFORE_PAYDAY':
        cutOffValue = payrollCutoffDays;
        break;
      case 'N_BUSINESS_DAYS_BEFORE_PAYDAY':
        cutOffValue = payrollCutoffDays;
        break;
      case 'N_DAY_OF_PERIOD':
        cutOffValue = payrollCutoffDays;
        break;
      case 'N_BUSINESS_DAY_OF_PERIOD':
        cutOffValue = payrollCutoffDays;
        break;
      default:
        cutOffValue = undefined;
    }

    let startingDays = 0;

    if (startingBalanceDays !== undefined) {
      if (startingBalanceDays === 100) {
        startingDays = 0;
      } else {
        startingDays = startingBalanceDays;
      }
    }

    createPayCalendar({
      name,
      ...(externalPayCalendarId?.trim() && { externalPayCalendarId: externalPayCalendarId.trim() }),
      companyId: company?.id || '',
      location: location || 'ENGLAND_WALES',
      currency,
      payFrequency: payFrequency || 'MONTHLY', // find the ? on payFrequency somewhere
      odpAllowancePercentage: customOdpAllowancePercentageValue ?? 0, // also mandatory
      globalAllowancePercentage: globalAllowancePercentageValue,
      startingBalanceDays: startingDays,
      defaultAnnualGrossSalary: Number.isNaN(defaultAnnualGrossSalaryParsed)
        ? undefined
        : defaultAnnualGrossSalaryParsed,
      startDate,
      payrollCutoff: payrollCutoff || 'N_DAYS_BEFORE_PAYDAY', // no default
      payrollCutoffDays: cutOffValue || 0,
      payrollCutoffCustomDay:
        payrollCutoff === 'CUSTOM_DAY' || payrollCutoff === 'CUSTOM_DAY_IN_ARREARS'
          ? payrollCutoffCustomDay
          : undefined,
      payday: payday || 'LAST_DAY_OF_PERIOD', // no default
      paydayLastCustomWeekday:
        payday === 'LAST_CUSTOM_WEEKDAY' ? paydayLastCustomWeekday : undefined,
      ...(paydayFirstCustomWeekday && { paydayFirstCustomWeekday }), // NEW: First custom weekday for payday
      ...(paydayCustomDayValue && { paydayCustomDay: paydayCustomDayValue }), // was CUSTOM_DAY and CUSTOM_DAY_IN_ARREARS // // maps to: N_DAY_OF_PERIOD, N_BUSINESS_DAY_OF_PERIOD, CUSTOM_DAY, CUSTOM_DAY_IN_ARREARS
      workingDays,
      payType: payType || 'IN_PERIOD', // check default
      ...(payrollCutoffFixedDay && { payrollCutoffFixedDay }),
      ...(paydayFixedDay && { paydayFixedDay }),
      paydayNonBusinessDayFailover: paydayNonBusinessDayFailover || 'NO_CHANGE',
      cutoffNonBusinessDayFailover: cutOffNonBusinessDayFailover || 'NO_CHANGE',
      fullPeriodStartingBalance: startingBalanceDays === 100,
      accrualPeriod: accrualPeriod || 'PAYDAY_TO_PAYDAY', // NEW: Accrual period configuration (default to PAYDAY_TO_PAYDAY)
      salariedDeductionsRequestMadeAvailableConfig: (salariedDeductionsRequestMadeAvailableConfig ||
        'DEDUCTIONS_PAY_CYCLE_PAYDAY') as ExternalDeductionsConfigurations, // NEW: Salaried deductions config
      nonSalariedDeductionsRequestMadeAvailableConfig:
        (nonSalariedDeductionsRequestMadeAvailableConfig ||
          'DEDUCTIONS_PAY_CYCLE_PAYDAY') as ExternalDeductionsConfigurations, // NEW: Non-salaried deductions config
    });
  };

  const startDateParsed = parse(startDate || '2023-04-14', 'yyyy-MM-dd', new Date());

  const resolvedPayType: string = payType === 'IN_ARREARS' ? 'in arrears' : 'in period';

  // Now we have multiple pay frequencies, we have to set the number of cycles by type

  let numberOfCycles: number;

  switch (payFrequency) {
    case 'MONTHLY':
      numberOfCycles = 12;
      break;
    case 'FOUR_WEEKLY':
      numberOfCycles = 13;
      break;
    case 'FORTNIGHTLY':
      numberOfCycles = 26;
      break;
    case 'WEEKLY':
      numberOfCycles = 52;
      break;
    default:
      numberOfCycles = 0;
  }

  // Now we have many different types of cut off, we have to set values for this

  let payrollCutoffDescription: string;
  const dayOfPeriod = payrollCutoffDays;
  const businessDayOfPeriod = payrollCutoffDays;
  const dayOfCutoffFixedDay = getDate(new Date(payrollCutoffFixedDay ?? '1970-01-01'));

  switch (payrollCutoff) {
    case 'N_DAYS_BEFORE_PAYDAY':
      // Using nullish coalescing to provide a default value of 0.
      payrollCutoffDescription = `${payrollCutoffDays} ${
        payrollCutoffDays === 1 ? ' day' : ' days'
      } before payday.`;
      break;

    case 'N_BUSINESS_DAYS_BEFORE_PAYDAY':
      payrollCutoffDescription = `${payrollCutoffDays} ${
        payrollCutoffDays === 1 ? ' business day' : ' business days'
      } before payday.`;
      break;

    case 'N_DAY_OF_PERIOD':
      // For day of period, append the ordinal suffix.
      payrollCutoffDescription = `on the ${dayOfPeriod}${
        payrollCutoffDays !== undefined ? getOrdinalSuffix(payrollCutoffDays) : ''
      } day of the period.`;
      break;

    case 'N_BUSINESS_DAY_OF_PERIOD':
      payrollCutoffDescription = `${businessDayOfPeriod}${
        payrollCutoffDays !== undefined ? getOrdinalSuffix(payrollCutoffDays) : ''
      } business day of the period.`;
      break;

    case 'FIXED_DAY':
      payrollCutoffDescription = `on a fixed day, the ${dayOfCutoffFixedDay}${getOrdinalSuffix(
        dayOfCutoffFixedDay
      )} of each month`;
      break;

    default:
      payrollCutoffDescription = 'Error working out what the payroll cutoff text should be';
  }

  return (
    <Modal
      isOpen={isOpen}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      onClose={onClose}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text as="h6">Pay calendar overview</Text>
        </ModalHeader>
        <ModalBody mt="20px">
          <Stack spacing="20px">
            <Text color="brand.mutedPurple" textStyle="smallMedium">
              This will generate <strong>{numberOfCycles}</strong> pay cycles. Please confirm the
              details:
            </Text>
            <Stack
              bg="brand.lightGray2"
              p="10px"
              borderRadius="5px"
              border="1px solid"
              borderColor="brand.lightGray1"
              spacing="10px"
              color="brand.darkGray"
              textStyle="smallRegular"
            >
              <Text>
                This pay calendar will run{' '}
                <Text
                  as="span"
                  color="brand.purple"
                  textStyle="smallMedium"
                  textTransform="lowercase"
                >
                  {PayFrequency[payFrequency ?? 'MONTHLY']}, {[resolvedPayType]}.
                </Text>
              </Text>
              <Text>
                It will start on{' '}
                <Text as="span" color="brand.purple" textStyle="smallMedium">
                  {isValid(startDateParsed) && format(startDateParsed, 'MMMM d, yyyy')}.
                </Text>
              </Text>
              <Text>
                Cut-off occurs{' '}
                <Text as="span" color="brand.purple" textStyle="smallMedium">
                  {payrollCutoffDescription}
                </Text>
              </Text>
              <Text>
                Employees will accrue{' '}
                <Text as="span" color="brand.purple" textStyle="smallMedium">
                  {`${customOdpAllowancePercentageValue}%`}
                </Text>{' '}
                on a daily basis with Aslan.
              </Text>
              {globalAllowancePercentage && (
                <Text>
                  Users can set and spend up to{' '}
                  <Text as="span" color="brand.purple" textStyle="smallMedium">
                    {`${
                      globalAllowancePercentage === 200
                        ? customGlobalAllowancePercentage || 0
                        : globalAllowancePercentage
                    }%`}
                  </Text>{' '}
                  per pay cycle of their salary.
                </Text>
              )}
              {accrualPeriod && (
                <Text>
                  Accruals will run{' '}
                  <Text as="span" color="brand.purple" textStyle="smallMedium">
                    {AccrualPeriodType[
                      accrualPeriod as keyof typeof AccrualPeriodType
                    ]?.toLowerCase() || '-'}
                  </Text>
                  .
                </Text>
              )}
              {salariedDeductionsRequestMadeAvailableConfig && (
                <Text>
                  MAT for salaried workers is on{' '}
                  <Text as="span" color="brand.purple" textStyle="smallMedium">
                    {DeductionsConfigurations[
                      salariedDeductionsRequestMadeAvailableConfig as keyof typeof DeductionsConfigurations
                    ] || '-'}
                  </Text>
                  .
                </Text>
              )}
              {nonSalariedDeductionsRequestMadeAvailableConfig && (
                <Text>
                  MAT for non-salaried workers is on{' '}
                  <Text as="span" color="brand.purple" textStyle="smallMedium">
                    {DeductionsConfigurations[
                      nonSalariedDeductionsRequestMadeAvailableConfig as keyof typeof DeductionsConfigurations
                    ] || '-'}
                  </Text>
                  .
                </Text>
              )}
              <Text>
                The users starting balance will be{' '}
                <Text as="span" color="brand.purple" textStyle="smallMedium">
                  {`${
                    startingBalanceDays === 100 ? 'all ' : startingBalanceDays || 0
                  } working days.`}
                </Text>
              </Text>
            </Stack>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="outline" onClick={handleEdit}>
            Edit
          </Button>
          <Button
            size="sm"
            rightIcon={<CheckOutline boxSize="20px" />}
            onClick={handleConfirm}
            isLoading={isCreatingPayCalendar}
          >
            Confirm
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default CreatePayCalendarOverviewModal;
